/**
 * DetailHeader — header for account-detail views.
 * Adds an overflow menu with:
 *   • Set balance
 *   • Edit
 *   • Delete
 * The menu is only shown when at least one handler is provided.
 */
import React from 'react';
import { ArrowLeft, MoreHorizontal, Pencil, Trash2, Coins } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import AccountAvatar from './AccountAvatar';
import { ACCOUNT_TYPE_LABEL } from '@/lib/accountsDemo';

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

export default function DetailHeader({
  account,
  onBack,
  balance,
  balanceLabel,
  balanceColor,
  onEdit,
  onDelete,
  onSetBalance,
}) {
  const hasMenu = onEdit || onDelete || onSetBalance;

  return (
    <div className="flex items-start gap-4 mb-6" data-testid="accounts-detail-header">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-charcoal font-medium transition-colors mt-1 flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded"
        data-testid="accounts-detail-back"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </button>
      <div className="flex-1">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <AccountAvatar account={account} size="lg" />
            <div className="min-w-0">
              <h2 className="font-display text-xl font-medium text-charcoal leading-tight truncate">{account.name}</h2>
              <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground truncate">
                <span className="truncate">{account.institution || account.subtype_label || ''}</span>
                {account.subtype_label && account.institution && account.subtype_label !== account.institution && (
                  <>
                    <span className="w-0.5 h-0.5 rounded-full bg-muted-foreground/40 shrink-0" />
                    <span className="truncate">{ACCOUNT_TYPE_LABEL[account.type] || account.subtype_label}</span>
                  </>
                )}
                {account.account_number && (
                  <>
                    <span className="w-0.5 h-0.5 rounded-full bg-muted-foreground/40 shrink-0" />
                    <span className="num truncate opacity-70">{account.account_number}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-start gap-2 shrink-0">
            <div className="text-right">
              {balanceLabel && (
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-1">{balanceLabel}</p>
              )}
              <p
                className="num font-display text-2xl font-medium"
                style={{ color: balanceColor || '#1E1E1E' }}
                data-testid="accounts-detail-balance"
              >
                {balance}
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Updated {fmtDate(account.last_updated)}</p>
            </div>

            {hasMenu && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="h-8 w-8 rounded-lg hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-charcoal transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                    data-testid="accounts-detail-menu"
                    aria-label="Account actions"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  {onSetBalance && (
                    <DropdownMenuItem onClick={onSetBalance} data-testid="accounts-detail-menu-setbalance">
                      <Coins className="h-3.5 w-3.5 mr-2" /> Set balance
                    </DropdownMenuItem>
                  )}
                  {onEdit && (
                    <DropdownMenuItem onClick={onEdit} data-testid="accounts-detail-menu-edit">
                      <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
                    </DropdownMenuItem>
                  )}
                  {onDelete && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={onDelete}
                        className="text-destructive focus:text-destructive"
                        data-testid="accounts-detail-menu-delete"
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
